import { useState, useEffect, useCallback } from 'react';
import { batchHistoryAPI } from '@/services/batchHistoryAPI';
import type { BatchRecord, BatchStats } from '@/services/batchHistoryAPI';
import { logger } from '@/utils/logger';

interface UseBatchHistoryOptions {
  limit?: number;
  offset?: number;
  autoLoad?: boolean;
}

interface UseBatchHistoryReturn {
  batches: BatchRecord[];
  stats: BatchStats | null;
  loading: boolean;
  error: string | null;
  totalCount: number;
  loadBatches: () => Promise<void>;
  loadStats: () => Promise<void>;
  refreshData: () => Promise<void>;
  getBatchById: (batchId: string) => Promise<BatchRecord | null>;
}

export const useBatchHistory = (options: UseBatchHistoryOptions = {}): UseBatchHistoryReturn => {
  const { limit = 50, offset = 0, autoLoad = true } = options;
  
  const [batches, setBatches] = useState<BatchRecord[]>([]);
  const [stats, setStats] = useState<BatchStats | null>(null);
  const [loading, setLoading] = useState(autoLoad);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const loadBatches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      logger.info('Loading batch history:', { limit, offset });
      
      const response = await batchHistoryAPI.getBatchHistory(limit, offset);
      
      // Sort by creation date (newest first)
      const sortedBatches = response.batches.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      
      setBatches(sortedBatches);
      setTotalCount(response.total);
      
      logger.info('Batch history loaded successfully:', { count: sortedBatches.length, total: response.total });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load batch history';
      setError(errorMessage);
      logger.error('Failed to load batch history:', err);
    } finally {
      setLoading(false);
    }
  }, [limit, offset]);

  const loadStats = useCallback(async () => {
    try {
      setError(null);
      
      logger.info('Loading batch statistics');
      
      const batchStats = await batchHistoryAPI.getBatchStats();
      setStats(batchStats);
      
      logger.info('Batch statistics loaded successfully:', batchStats);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load batch statistics';
      setError(errorMessage);
      logger.error('Failed to load batch statistics:', err);
      
      // Set empty stats on error
      setStats({
        total: 0,
        completed: 0,
        failed: 0,
        processing: 0,
        totalRecordsProcessed: 0,
        successfulRecords: 0
      });
    }
  }, []);

  const refreshData = useCallback(async () => {
    await Promise.all([loadBatches(), loadStats()]);
  }, [loadBatches, loadStats]);

  const getBatchById = useCallback(async (batchId: string): Promise<BatchRecord | null> => {
    try {
      logger.info('Fetching batch by ID:', { batchId });
      
      const batch = await batchHistoryAPI.getBatchById(batchId);
      
      if (batch) {
        logger.info('Batch fetched successfully:', { batchId });
      } else {
        logger.warn('Batch not found:', { batchId });
      }
      
      return batch;
    } catch (err) {
      logger.error('Failed to fetch batch by ID:', { batchId, error: err });
      throw err;
    }
  }, []);

  useEffect(() => {
    if (autoLoad) {
      refreshData();
    }
  }, [autoLoad, refreshData]);

  return {
    batches,
    stats,
    loading,
    error,
    totalCount,
    loadBatches,
    loadStats,
    refreshData,
    getBatchById
  };
};

// Helper hook for just getting batch statistics
export const useBatchStats = () => {
  const [stats, setStats] = useState<BatchStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const batchStats = await batchHistoryAPI.getBatchStats();
      setStats(batchStats);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load statistics';
      setError(errorMessage);
      
      // Set empty stats on error
      setStats({
        total: 0,
        completed: 0,
        failed: 0,
        processing: 0,
        totalRecordsProcessed: 0,
        successfulRecords: 0
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return { stats, loading, error, refreshStats: loadStats };
};

// Helper hook for recent batches (useful for dashboard widgets)
export const useRecentBatches = (limit: number = 5) => {
  const [recentBatches, setRecentBatches] = useState<BatchRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRecentBatches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await batchHistoryAPI.getBatchHistory(limit);
      const sortedBatches = response.batches.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      
      setRecentBatches(sortedBatches);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load recent batches';
      setError(errorMessage);
      setRecentBatches([]);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    loadRecentBatches();
  }, [loadRecentBatches]);

  return { recentBatches, loading, error, refreshRecentBatches: loadRecentBatches };
};